import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { PageTitle } from '../components/Layouts.js';
import {
  AI,
  CLASSIC,
  Legend,
  useFormat,
  hi,
  lo,
  useModernization,
  type ModernPackage,
} from '../components/modernization.js';
import { Card, LinkButton, LoadError, Loading } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { BackButton } from '../components/BackButton.js';

/** The packages side by side (T-39): cost classic vs. with AI, effect, payback, risks. */
export function ModernizationCompare() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const f = useFormat();
  const query = useModernization(siteId);

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const packages = query.data.packages;
  const max = Math.max(1, ...packages.map((p) => hi(p.cost.classic)));

  const rows: [string, (p: ModernPackage) => string][] = [
    ['modern.compare.includes', (p) => t(`modern.packages.${p.key}.summary`)],
    [
      'modern.compare.hours',
      (p) => `${f.hours(p.hours.classic)} · ${f.hours(p.hours.ai)} ${t('modern.withAi')}`,
    ],
    [
      'modern.compare.cost',
      (p) => `${f.euroRange(p.cost.classic)} · ${f.euroRange(p.cost.ai)} ${t('modern.withAi')}`,
    ],
    [
      'modern.compare.speed',
      (p) =>
        p.effect.lcpMs
          ? t('modern.compare.lcp', {
              from: f.seconds(lo(p.effect.lcpMs)),
              to: f.seconds(hi(p.effect.lcpMs)),
            })
          : t('modern.compare.noSpeed'),
    ],
    ['modern.compare.gain', (p) => t(`modern.packages.${p.key}.gain`)],
    [
      'modern.payback.label',
      (p) =>
        p.paybackMonths
          ? f.months(p.paybackMonths)
          : p.uplift
            ? t('modern.payback.needFigures')
            : t(`modern.packages.${p.key}.paybackNote`),
    ],
    ['modern.compare.access', (p) => t(`modern.packages.${p.key}.access`).split('\n')[0] ?? ''],
    ['modern.compare.risks', (p) => t(`modern.packages.${p.key}.risks`)],
    ['modern.compare.forWhom', (p) => t(`modern.packages.${p.key}.forWhom`)],
  ];

  return (
    <>
      <p className="mb-4">
        <BackButton to={`/sites/${siteId}/modernization`}>{t('modern.back')}</BackButton>
      </p>
      <PageTitle>{t('modern.compare.title')}</PageTitle>
      <p className="-mt-4 mb-6 max-w-prose text-slate-700">{t('modern.compare.intro')}</p>

      <Card className="mb-6 flex flex-col gap-3">
        <h2 className="text-xl font-semibold">{t('modern.compare.costTitle')}</h2>
        <div
          className="grid gap-6"
          style={{ gridTemplateColumns: `repeat(${Math.max(packages.length, 1)}, minmax(0, 1fr))` }}
          role="img"
          aria-label={packages
            .map(
              (p) =>
                `${t(`modern.packages.${p.key}.title`)}: ${f.euro(hi(p.cost.classic))} / ${f.euro(hi(p.cost.ai))}`,
            )
            .join('; ')}
        >
          {packages.map((p) => (
            <div key={p.key} className="flex flex-col items-center gap-2">
              <div className="flex h-56 items-end gap-3">
                {(
                  [
                    [hi(p.cost.classic), CLASSIC],
                    [hi(p.cost.ai), AI],
                  ] as const
                ).map(([v, color]) => (
                  <div key={color} className="flex flex-col items-center justify-end gap-1">
                    <span className="text-xs font-semibold tabular-nums">{f.euro(v)}</span>
                    <span
                      className="block w-12 rounded-t"
                      style={{ height: `${Math.max(2, (v / max) * 200)}px`, background: color }}
                    />
                  </div>
                ))}
              </div>
              <span className="text-center text-sm font-semibold">
                {t(`modern.packages.${p.key}.title`)}
              </span>
            </div>
          ))}
        </div>
        <Legend />
      </Card>

      <Card className="mb-6 overflow-x-auto">
        <table className="w-full min-w-[40rem] border-collapse text-left">
          <thead>
            <tr>
              <th scope="col" className="w-44" />
              {packages.map((p) => (
                <th key={p.key} scope="col" className="px-3 pb-3 align-bottom text-lg">
                  {p.recommended ? (
                    <span className="mb-1 block w-fit rounded-full bg-green-100 px-2.5 py-0.5 text-sm text-green-900">
                      {t('modern.recommended')}
                    </span>
                  ) : null}
                  {t(`modern.packages.${p.key}.title`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, cell]) => (
              <tr key={label} className="border-t border-slate-200 align-top">
                <th
                  scope="row"
                  className="py-3 pr-3 text-sm font-bold tracking-wide text-slate-700 uppercase"
                >
                  {t(label)}
                </th>
                {packages.map((p) => (
                  <td key={p.key} className="px-3 py-3 tabular-nums">
                    {cell(p)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-slate-200">
              <td />
              {packages.map((p) => (
                <td key={p.key} className="px-3 py-3">
                  <LinkButton to={`/sites/${siteId}/modernization/${p.key}`} variant="secondary">
                    {t('modern.details')}
                  </LinkButton>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </Card>
      <p className="text-sm text-slate-700">{t('modern.disclaimer')}</p>
    </>
  );
}
